import { types, Instance } from 'mobx-state-tree';
import { Question, IQuestion } from './Question';
import { toJS } from 'mobx';
import InvokerType from './InvokerType';
import { RTModel } from './RTModel';

export const PollResultRow = types.model('PollResultRow', {
  count: 0,
  perc: 0,
  users: types.array(types.string),
});
export interface IPollResultRow extends Instance<typeof PollResultRow> {}

export const PollResult = types
  .model('PollResult', {
    total: 0,
    results: types.map(PollResultRow),
  })
  .views((self) => ({
    // The server aggregates answers keyed by strip().lower() of the submitted
    // string, so an MCQ/MSQ row for option "C" arrives under "c". Options keep
    // their original case, so a plain results.get(option.name) misses.
    rowForOption(optionName: string) {
      return self.results.get(optionName) || self.results.get(optionName.trim().toLowerCase());
    },
  }));
export interface IPollResult extends Instance<typeof PollResult> {}

export const PollStore = types
  .model('PollStore', {
    activityName: '',
    // Identifies one activation of a poll, so a second showing of the same
    // question is a distinct round. Empty when the API predates it.
    pollRound: '',
    question: types.maybeNull(Question),
    result: types.maybeNull(PollResult),
    minimize: false,
    roomName: types.string,
    submitInvoke: InvokerType,
    clearInvoke: InvokerType,
    setQuestionInvoke: InvokerType,
    setQuestionByNameInvoke: InvokerType,
    showResultInvoke: InvokerType,
    hideResultInvoke: InvokerType,
  })
  .actions((self) => ({
    setQuestion(question: IQuestion, activityName: string) {
      return self.setQuestionInvoke.invoke({ question: toJS(question), activity_name: activityName });
    },
    setQuestionByName(name: string) {
      return self.setQuestionByNameInvoke.invoke({ name });
    },
    // `pollRound` is the round the caller had on screen when the answer was
    // given, NOT self.pollRound. A coach's switch updates this store before
    // React re-renders, and the box does not remount, so the old Submit button
    // is still live for a tick — reading the current value there would relabel
    // the old answer with the new round, the server would accept it, and the
    // student would land on the waiting screen for a question they never saw.
    // That is the lockout this whole change exists to close.
    submitAnswer(answers: Array<string>, pollRound: string) {
      return self.submitInvoke.invoke({ answers, poll_round: pollRound });
    },
    clearQuestion() {
      return self.clearInvoke.invoke();
    },
    showResults(showUsers: boolean) {
      return self.showResultInvoke.invoke({ show_users: showUsers });
    },
    hideResults() {
      return self.hideResultInvoke.invoke();
    },
    setMinimize(val: boolean) {
      self.minimize = val;
    },
  }))
  .views((self) => ({
    get isActive() {
      return !!self.question;
    },
  }));

export interface IPollStore extends Instance<typeof PollStore> {}

export const PollUserStore = RTModel.named('PollUserStore').props({
  answerMap: types.map(types.array(types.string)),
  // answerMap is room-scoped and holds one entry per student, so an entry can
  // be left over from a round that is no longer running. This records the round
  // each entry was given under; see PollBox.
  answeredRound: types.map(types.string),
});

export interface IPollUserStore extends Instance<typeof PollUserStore> {}

export const PollResultStore = RTModel.named('PollResultStore').props({
  result: types.maybeNull(PollResult),
});

export interface IPollResultStore extends Instance<typeof PollResultStore> {}
